import { useState } from 'react';

export default function WorkflowBuilder() {
  const [selectedTool, setSelectedTool] = useState<string | null>(null);

  const taskTypes = [
    { id: 'start', label: 'Start', icon: 'ri-play-circle-line', color: 'green' },
    { id: 'task', label: 'Task', icon: 'ri-file-list-line', color: 'blue' },
    { id: 'decision', label: 'Decision', icon: 'ri-git-branch-line', color: 'yellow' },
    { id: 'end', label: 'End', icon: 'ri-stop-circle-line', color: 'red' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Workflow Builder</h1>
            <p className="text-gray-600">Create automated workflows and business processes</p>
          </div>

          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <i className="ri-play-line mr-2"></i>
              Test Workflow
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              <i className="ri-save-line mr-2"></i>
              Save Workflow
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Task Toolbox */}
          <div className="col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Tasks</h3>
              <div className="space-y-2">
                {taskTypes.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => setSelectedTool(task.id)}
                    className={`w-full flex flex-col items-center px-4 py-3 rounded-lg transition-colors ${
                      selectedTool === task.id
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <i className={`${task.icon} text-${task.color}-600 text-2xl mb-2`}></i>
                    <span className="text-xs font-medium text-gray-900">{task.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="col-span-7">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Workflow Canvas</h3>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <i className="ri-zoom-in-line"></i>
                  </button>
                  <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <i className="ri-zoom-out-line"></i>
                  </button>
                  <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <i className="ri-fullscreen-line"></i>
                  </button>
                </div>
              </div>

              <div className="min-h-[600px] border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                <div className="h-full flex items-center justify-center text-center">
                  <div className="max-w-md">
                    <i className="ri-git-branch-line text-6xl text-gray-300 mb-4"></i>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Build Your Workflow</h3>
                    <p className="text-gray-600 mb-4">
                      Select tasks from the left panel and drag them onto the canvas to create your workflow
                    </p>

                    {/* Sample workflow nodes */}
                    <div className="flex items-center justify-center space-x-4 mt-8">
                      <div className="bg-white border-2 border-green-500 rounded-lg p-4 shadow-sm">
                        <i className="ri-play-circle-line text-green-600 text-2xl mb-2"></i>
                        <p className="text-xs font-medium">Start</p>
                      </div>
                      <i className="ri-arrow-right-line text-gray-400"></i>
                      <div className="bg-white border-2 border-blue-500 rounded-lg p-4 shadow-sm">
                        <i className="ri-file-list-line text-blue-600 text-2xl mb-2"></i>
                        <p className="text-xs font-medium">Task</p>
                      </div>
                      <i className="ri-arrow-right-line text-gray-400"></i>
                      <div className="bg-white border-2 border-red-500 rounded-lg p-4 shadow-sm">
                        <i className="ri-stop-circle-line text-red-600 text-2xl mb-2"></i>
                        <p className="text-xs font-medium">End</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Panel */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Task Properties</h3>
              <div className="text-center py-8">
                <i className="ri-settings-line text-4xl text-gray-300 mb-2"></i>
                <p className="text-sm text-gray-600">Select a task to edit its properties</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 mt-6">
              <h3 className="font-semibold text-gray-900 mb-4">Workflow Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Tasks</span>
                  <span className="font-medium text-gray-900">0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Connections</span>
                  <span className="font-medium text-gray-900">0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="text-yellow-600 font-medium">Draft</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
